import { useCallback, useRef, useState } from "react";
import { useLanguage } from "@/shared/hooks/use-language";
import {
  MAX_PAGES,
  getUserAccessPage,
  listRemainingUserAccesses,
  type UserAccess,
  type UserAccessFilters,
} from "@/features/auth/api/user-access.ts";
import type { ScopeContext } from "@/features/auth/model/types";
import { DebugLogger } from "@/shared/lib/debug.ts";
import { isUnreadableResponseError } from "@/shared/lib/response-shape.ts";

export interface AccessListState {
  accesses: UserAccess[];
  // Whether this listing has ever come back from the backend. An untouched listing is
  // indistinguishable from an empty one by its rows alone, and the difference is the whole
  // claim: "nobody has access" is a statement about a scope, and a listing that was never
  // asked has not earned it. Every path that cannot issue a request leaves this false.
  //
  // Deliberately *not* set on a failed first load (see `loadFirstPage`'s catch) -- an error
  // is not an answer. A caller that needs to know "the first load is done, whichever way it
  // went" wants `hasSettled` below instead; conflating the two here would make the error
  // branch fall back to "not asked yet" the moment `error` is cleared without a refetch.
  hasAnswered: boolean;
  // Whether the first load this listing instance ever ran has reached a terminal outcome --
  // success, failure, or a caller-side determination that no request is needed at all
  // (`clear()`). Unlike `hasAnswered`, this is true after an error too, which is the whole
  // reason it exists: a one-shot consumer (the Access screen's hand-off latch) needs to know
  // the load is *done*, not that it went well, or it waits forever past a failed load and
  // fires later on an unrelated successful reload. False again the moment a fresh load
  // starts (`beginLoading`, `loadFirstPage`'s pre-request reset), since a re-load is a new
  // outcome to wait for.
  hasSettled: boolean;
  cursor: string;
  truncated: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  // How many pages this listing has taken. The cursor repeating is not the only way a
  // backend fails to advance -- a fresh cursor over an empty (or fully deduped) page is
  // neither an error nor a stall, and without a budget the sentinel re-fires on it for ever.
  pagesLoaded: number;
  // A first-page failure owns the whole area; a failure loading page N must not throw away
  // the N-1 pages already on screen, and must stop the automatic loader until the user asks
  // again -- otherwise the sentinel re-fires against the same cursor without end.
  error: string | null;
  loadMoreError: string | null;
}

const EMPTY_ACCESS_LIST: AccessListState = {
  accesses: [],
  hasAnswered: false,
  hasSettled: false,
  cursor: "",
  truncated: false,
  isLoading: false,
  isLoadingMore: false,
  pagesLoaded: 0,
  error: null,
  loadMoreError: null,
};

export interface AccessListing {
  state: AccessListState;
  // Marks the listing busy before an out-of-band step that has to answer before the first
  // page can be asked for -- the holder-access probe. Without it the screen would hold the
  // previous scope's rows on screen for the length of the probe and only then skeleton.
  beginLoading: () => void;
  loadFirstPage: (scope: ScopeContext) => Promise<void>;
  loadMore: (loadEverything: boolean) => void;
  retryLoadMore: () => void;
  continuePastCap: () => void;
  clear: () => void;
}

function dedupeAccesses(accesses: UserAccess[]): UserAccess[] {
  const byId = new Map<string, UserAccess>();
  accesses.forEach(access => byId.set(access.id, access));
  return [...byId.values()];
}

export interface AccessListingOptions {
  sessionId: string;
  // Read at request time, not reacted to: changing this does not refetch. The caller owns
  // that, because only the caller knows whether a filter change should reset the listing or
  // start a second one -- the Access screen refetches from its own effect, the Integrations
  // screen passes a module constant that never changes. A consumer that expects reactivity
  // here gets a listing that silently never updates, so it is stated rather than implied.
  filters: UserAccessFilters;
  // Whether the listing counts as loading before anything has been asked for. True where a
  // screen paints a full-page skeleton until its primary listing answers.
  initialLoading?: boolean;
}

/**
 * One scope's User-Access listing: its pages, its cursor, and every state the cursor can
 * leave it in.
 *
 * Held here rather than in a screen because two screens list the same resource under
 * different filters -- Access Management under a tab's filter set, Integrations under a
 * fixed integration filter -- and every trap this state machine avoids (a stale page landing
 * after a revoke, an echoed cursor, the page budget, a mid-listing failure that must not
 * discard what is already on screen) is a property of the listing, not of either screen.
 *
 * A caller with two scopes calls this twice. The generation counter is per instance for that
 * reason: one shared counter would have each listing invalidate the other's in-flight first
 * page, since they load concurrently.
 */
export function useAccessListing({
  sessionId,
  filters,
  initialLoading = false,
}: AccessListingOptions): AccessListing {
  const { t } = useLanguage();
  const [state, setState] = useState<AccessListState>({
    ...EMPTY_ACCESS_LIST,
    isLoading: initialLoading,
  });
  // Bumped by every writer that resets the listing. An async page carries the generation it
  // was started under and is discarded if anything reset the listing meanwhile -- otherwise
  // a slow page landing after a revoke re-renders the access that was just removed.
  const generation = useRef(0);
  // The scope the listing was last loaded against, so following the cursor cannot drift onto
  // another scope midway through a listing.
  const scopeRef = useRef<ScopeContext | null>(null);
  // The re-entrancy guard, held in a ref rather than read off `state`. Two callers fire from
  // the same render -- the search drain and the sentinel observer -- and a guard that reads
  // the render closure sees `isLoadingMore: false` in both, because the write that sets it
  // does not change the state that closure captured. Both requests then land, the last write
  // wins, and a completed listing can be handed back a spent cursor: the sentinel re-pages
  // over rows the dedupe discards until the page budget marks it truncated, so a fully
  // loaded listing ends up reporting itself as not fully loaded, with a Continue button.
  const isPaging = useRef(false);

  // Called by every writer that invalidates the listing -- `beginLoading`, `clear` and
  // `loadFirstPage` -- so a reset never leaves the latch held for the length of an abandoned
  // request. Without it, a reset landing inside a drain (a tab click, a scope change, a
  // cancel refreshing the listings) left the fresh listing reporting itself not fully loaded
  // with every paging control inert: the generation drop returns before any state write, so
  // `loadMore` keeps its identity, the sentinel effect does not re-run, and an
  // already-intersecting sentinel never fires again.
  //
  // `loadMore`'s own `finally` also calls this, but only when its own generation is still
  // current -- see the `started === generation.current` guard there. A reset call here
  // bumps the generation and releases unconditionally, which means it can release a latch a
  // NEWER page has since set: drain A stalls, a reset lands mid-flight and bumps the
  // generation to N+1, the sentinel fires page B under N+1 and sets the latch again, and only
  // then does A's request settle. A's `finally` must not clear that latch -- it was never
  // A's to release, and doing so anyway would let a third request start while B still thinks
  // it holds paging alone.
  const releasePaging = useCallback(() => {
    isPaging.current = false;
  }, []);

  const readableError = useCallback(
    (err: unknown, fallback: string) => {
      if (isUnreadableResponseError(err)) return t("errors.unreadableResponse");
      return err instanceof Error ? err.message : fallback;
    },
    [t],
  );

  const update = useCallback(
    (patch: Partial<AccessListState>) => setState(previous => ({ ...previous, ...patch })),
    [],
  );

  // A full reset, not a two-field patch. `truncated` and `loadMoreError` describe the
  // *previous* listing's paging outcome, and AccessList renders both as siblings of the
  // loading branch -- so a patch left the old failure notice, with a live Retry, sitting on
  // top of the skeleton of the listing that replaced it. `isLoading` still hides the old
  // rows, which is what this was added for.
  const beginLoading = useCallback(() => {
    // Bumps the generation and releases the latch, like `clear` and `loadFirstPage`. It is
    // the third writer that invalidates the listing, and without these it was the one that
    // did neither: a page still in flight from the previous listing satisfied the generation
    // check and wrote its rows and cursor back over the reset state, for the width of the
    // holder probe.
    generation.current += 1;
    releasePaging();
    setState({ ...EMPTY_ACCESS_LIST, isLoading: true });
  }, [releasePaging]);

  const clear = useCallback(() => {
    generation.current += 1;
    releasePaging();
    // `hasSettled: true`, not the `EMPTY_ACCESS_LIST` default: `clear` is itself a terminal
    // outcome -- "this listing has no scope to ask about" -- not the start of a new attempt,
    // so a one-shot latch waiting on `hasSettled` must be released here too. Without it, a
    // caller that never gets far enough to call `loadFirstPage` (no holder id, denied holder
    // access) leaves the latch armed indefinitely, ready to fire later if the same listing
    // instance is ever settled some other way.
    setState({ ...EMPTY_ACCESS_LIST, hasSettled: true });
  }, [releasePaging]);

  const loadFirstPage = useCallback(
    async (scope: ScopeContext) => {
      generation.current += 1;
      releasePaging();
      const started = generation.current;
      scopeRef.current = scope;
      setState({ ...EMPTY_ACCESS_LIST, isLoading: true });
      try {
        const page = await getUserAccessPage(sessionId, scope, filters);
        if (started !== generation.current) return;
        setState({
          ...EMPTY_ACCESS_LIST,
          accesses: page.accesses,
          hasAnswered: true,
          hasSettled: true,
          cursor: page.cursor,
          pagesLoaded: 1,
        });
      } catch (err) {
        DebugLogger.error("Failed to fetch accesses", {
          scopeType: scope.scopeType,
          error: err instanceof Error ? err.message : "Unknown error",
        });
        if (started === generation.current) {
          // Not `hasAnswered`: nothing came back to answer with. The error branch is what
          // renders here, and leaving the flag false means that if an error is ever cleared
          // without a refetch -- the shape `retryLoadMore` already has -- the listing falls
          // back to "not asked yet" rather than to "nobody has access".
          //
          // `hasSettled: true` regardless: an error is still a terminal outcome for this
          // load attempt. A one-shot latch that only watched `hasAnswered` used to treat a
          // failed load as indistinguishable from "never asked" and wait past it, then fire
          // later on whatever reload happened to succeed -- see the Access screen's
          // hand-off effect, which reads `hasSettled` for exactly this reason.
          setState({
            ...EMPTY_ACCESS_LIST,
            hasSettled: true,
            error: readableError(err, t("access.loadFailed")),
          });
        }
        throw err;
      }
    },
    [sessionId, filters, readableError, releasePaging, t],
  );

  const loadMore = useCallback(
    async (loadEverything: boolean) => {
      // Every terminal condition is a guard, so no path re-enters: exhausted (no cursor),
      // already working, stopped by the page cap, or stopped by a failure the user has not
      // acknowledged. Clearing loadMoreError is an explicit click, which is what restarts it.
      if (isPaging.current) return;
      if (!state.cursor || state.isLoading || state.isLoadingMore) return;
      // Two terms, one rule. The automatic pager must stop on an unacknowledged failure and
      // on a spent budget, or the sentinel re-fires against the same doomed cursor for ever.
      // Neither may stop the drain, which since the search became opt-in is only ever a
      // click -- and the click is the acknowledgement, exactly as `continuePastCap` treats
      // one. Blocking it leaves the control the incomplete state offers doing nothing, which
      // is the trap the state was in to begin with.
      if (!loadEverything && (state.loadMoreError || state.truncated)) return;

      const scope = scopeRef.current;
      if (!scope) return;

      const started = generation.current;
      isPaging.current = true;
      update({ isLoadingMore: true, loadMoreError: null });
      try {
        // A drain past a spent budget restarts the count, or `MAX_PAGES - pagesLoaded` is
        // zero and the call returns without asking for anything -- the button would render,
        // be clickable, and do nothing, which is the defect one line up.
        const pagesBefore = loadEverything && state.truncated ? 0 : state.pagesLoaded;
        // A reset landing mid-drain (tab click, scope change, a revoke's refresh) must stop
        // the drain from issuing further pages, not only make its final result discardable.
        // `listRemainingUserAccesses` checks this before every page it asks for, so an
        // invalidated generation stops the loop on its next iteration instead of walking up
        // to `MAX_PAGES` further signed round-trips nothing on screen will read.
        const result = loadEverything
          ? await listRemainingUserAccesses(sessionId, scope, filters, state.cursor,
                                            MAX_PAGES - pagesBefore,
                                            () => generation.current === started)
          : await getUserAccessPage(sessionId, scope, filters, state.cursor);

        // A revoke, a role change or a refresh that landed while this page was in flight has
        // already replaced the listing this result was computed against. Applying it would
        // put the removed rows back, so the result is dropped instead.
        if (started !== generation.current) return;

        const pagesLoaded = pagesBefore
          + ("pagesLoaded" in result ? Number(result.pagesLoaded) : 1);
        const drainTruncated = "truncated" in result ? Boolean(result.truncated) : false;
        // Neither an error nor progress: the backend handed back the cursor we sent, so we
        // learned nothing about whether more exists. Stopping is right; calling the listing
        // complete is not, so it is reported as not fully loaded.
        const stalled = result.cursor === state.cursor;
        // `truncated` means "we stopped without learning whether more exists". A result that
        // carried no cursor answered that question, so spending the budget on the final page
        // is not truncation -- and reporting it as such strands the listing with no continue
        // control, since that control needs the cursor this path cleared.
        const overBudget = pagesLoaded >= MAX_PAGES && Boolean(result.cursor);

        update({
          accesses: dedupeAccesses([...state.accesses, ...result.accesses]),
          // The cursor is kept on every path that stops without finishing -- the budget, and
          // an echoed cursor too. Clearing it on a stall left the one state no control could
          // act on: `truncated` says the listing is incomplete, but Continue reads the
          // cursor, Load more reads `canAutoLoad`, and neither survives a cleared cursor. The
          // user was told the list was unfinished and given nothing that could finish it.
          //
          // Re-asking with an echoed cursor may well stall again, and that is the honest
          // offer: the backend told us nothing, so the only thing to do is ask again. What
          // it must not become is `listingComplete`, which would license "nobody has access"
          // over a listing we never finished.
          cursor: result.cursor,
          truncated: stalled || drainTruncated || overBudget,
          pagesLoaded,
          isLoadingMore: false,
          loadMoreError: null,
        });
      } catch (err) {
        DebugLogger.error("Failed to load more accesses", {
          error: err instanceof Error ? err.message : "Unknown error",
        });
        if (started !== generation.current) return;
        update({
          isLoadingMore: false,
          loadMoreError: readableError(err, t("access.loadMoreFailed")),
        });
      } finally {
        // Only when this request's own generation is still current -- see the comment on
        // `releasePaging`. A reset that landed while this request was in flight has already
        // released this latch itself and may have set it again for a newer page; clearing it
        // unconditionally here could drop a latch that page is relying on.
        if (started === generation.current) releasePaging();
      }
    },
    [sessionId, filters, state, update, readableError, releasePaging, t],
  );

  // Guarded on `isPaging` for the same reason `loadMore` itself is: this only clears a flag
  // a page fetch reads, not fetches a page, so AccessList's Retry button already stays inert
  // while `state.isLoadingMore` is true (see its comment). That covers the click; this covers
  // any other caller that reaches the hook directly and would otherwise clear the failure
  // notice for a request it never issued. `isPaging.current` is false whenever nothing is in
  // flight, so the legitimate click-with-no-drain-running path is untouched.
  const retryLoadMore = useCallback(() => {
    if (isPaging.current) return;
    update({ loadMoreError: null });
  }, [update]);

  // The page budget is a stop, not a verdict: the cursor is still live, so continuing is a
  // real escape from the truncated state rather than copy advising an action that cannot work.
  //
  // Guarded on `isPaging` for the same reason: this resets the budget unconditionally, and a
  // drain already in flight (started elsewhere -- "Search the rest" does not check `truncated`
  // by design) reads `pagesLoaded` and `truncated` at its own pace, so a call landing mid-drain
  // used to zero the count under a request already running. If that request then failed, the
  // catch below only patches `isLoadingMore`/`loadMoreError`, leaving `pagesLoaded: 0` with a
  // live cursor and no cap reachable again. AccessList's Continue button already stays inert
  // while `state.isLoadingMore` is true; this closes the same class for any caller that reaches
  // the hook directly. `isPaging.current` is false whenever nothing is in flight, so the
  // legitimate click-with-no-drain-running path is untouched.
  const continuePastCap = useCallback(() => {
    if (isPaging.current) return;
    update({ truncated: false, pagesLoaded: 0, loadMoreError: null });
  }, [update]);

  return { state, beginLoading, loadFirstPage, loadMore, retryLoadMore, continuePastCap, clear };
}
